<template>
  <div>
    <form>
      <label for="callName">Call name</label>
      <b-form-select id="callName"
                     v-model="cericProposalParameters.call_id"
                     :class="['mb-3', { 'custom-invalid-input-border': $v.cericProposalParameters.call_id.$error }]"
                     :options="callsOptions"
                     @input="$v.cericProposalParameters.call_id.$touch()"/>

      <div v-for="(value, key) in input_fields"
           :key="key">
        <label :for="key">{{ value.label }}</label>
        <span v-if="!value.required"
              class="grey--text"> - Optional</span>
        <b-form-input :id="key"
                      v-model="cericProposalParameters[key]"
                      :class="['mb-3', { 'custom-invalid-input-border': $v.cericProposalParameters[key].$error }]"
                      :disabled="value.disabled || false"
                      :min="value.min"
                      :readonly="false"
                      :type="value.type || 'text'"
                      v-bind="value.props"
                      @input="$v.cericProposalParameters[key].$touch()"/>
      </div>
      <div v-if="cericProposalParameters.infrastructure === 'synchrotron'">
        <label for="environment">Environment</label>
        <b-form-select id="environment" v-model="cericProposalParameters.beamlines"
                       :class="['mb-3', { 'custom-invalid-input-border': $v.cericProposalParameters.beamlines.$error }]"
                       :options="Object.keys(beamlines_config)"
                       @change="cericProposalParameters.end_station = ''"
                       @input="$v.cericProposalParameters.beamlines.$touch()"/>
        <label for="endStation">End station</label>
        <b-form-select id="endStation" v-model="cericProposalParameters.end_station"
                       :class="['mb-3', { 'custom-invalid-input-border': $v.cericProposalParameters.end_station.$error }]"
                       :options="beamlines_config[cericProposalParameters.beamlines]"
                       @input="$v.cericProposalParameters.end_station.$touch()"/>
      </div>
      <div>
        <b-form-group
          label="Attachments"
          label-for="attachments"
          label-size="md">
          <p class="grey--text mb-2">Use only PDF files.</p>
          <attachment-input id="attachments"
                            v-model="cericProposalParameters.attachments"
                            :attachment-url="attachment_url"
                            class="mt-1"
                            extensions="application/pdf"
                            multiple/>
        </b-form-group>
        <p v-if="!areAttachments && touchValidator"
           class="custom-text-danger error-text">
          At least one attachment should be added.
        </p>
      </div>
    </form>
    <h6 v-if="!validationState && touchValidator"
        class="custom-text-danger error-text">
      Fill all required fields to save.
    </h6>
  </div>
</template>

<script>
  import { cericProposalValidator } from '@/assets/validators/cericProposalValidator';
  import AttachmentInput from '@/components/AttachmentInput';
  // eslint-disable-next-line camelcase
  import beamlines_config from '@/json/beamlines';
  import { mapState } from 'vuex';

  export default {
    name: 'CericManageProposalForm',
    components: { AttachmentInput },
    props: {
      touchValidator: {
        type: Boolean,
        default: false,
      },
      saveButtonHandler: {
        type: Boolean,
        default: false,
      },
      objectId: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        /* eslint-disable camelcase */
        input_fields: {
          document_id: {
            label: 'CERIC proposal number',
            required: true,
            disabled: false,
          },
          title: {
            label: 'Title',
            value: '',
            required: true,
          },
          requested_hours: {
            label: 'Requested hours',
            required: true,
            type: 'number',
            min: 0,
          },
          shifts: {
            label: 'Granted shifts',
            required: true,
            type: 'number',
            min: 0,
          },
          main_author_name: {
            label: 'Main author\'s first name and last name (such order is required)',
            required: true,
          },
          main_author_email: {
            label: 'Main author\'s email (must be exactly the same as used by user in SUN)',
            required: true,
          },
        },
        cericProposalParameters: {
          call_id: '',
          document_id: '',
          infrastructure: '',
          attachments: [],
          title: '',
          requested_hours: '',
          shifts: '',
          main_author_name: '',
          main_author_email: '',
        },
        isAttachmentResponse: true,
        isPdfResponse: true,
        validationState: true,
        areAttachments: true,
        beamlines_config,

        // required by attachment-input
        attachment_url: '',
        /* eslint-enable camelcase */
      };
    },
    computed: {
      ...mapState('calls', ['calls']),
      callsOptions() {
        return this.calls.filter((call) => call.states?.name !== 'deleted')
          .map((call) => ({ value: this.getId(call), text: call.name }));
      },
    },
    validations: cericProposalValidator,
    watch: {
      cericProposalParameters: {
        handler() {
          this.validationState = !this.$v.$invalid;
          this.areAttachments = !this.$v.cericProposalParameters.attachments.$invalid;
          this.$emit('set-validation', !this.$v.$invalid);
        },
        deep: true,
      },
      saveButtonHandler: {
        handler() {
          // this method is used when button 'save proposal' in modal_ceric_proposal was clicked
          if (this.saveButtonHandler) {
            if (this.objectId) {
              this.updateProposal();
            } else {
              this.createProposal();
            }
          }
        },
      },
    },
    methods: {
      getProposal() {
        // this method is used if proposal has been given the _id (objectId) yet
        // it returns whole proposal, which must be converted properly to be displayed
        this.axios.get(`/documents/ceric/${this.objectId}`)
          .then((resp) => {
            const { document, error } = resp.data;
            if (document) {
              this.fillProposalData(document);
            } else {
              this.$notify({ type: 'error', title: error });
            }
          });
      },
      fillProposalData(document) {
        const documentSpecification = document.document_specification;
        /* eslint-disable camelcase */
        this.cericProposalParameters = {
          call_id: document.call_id,
          document_id: documentSpecification.document_id,
          infrastructure: documentSpecification.type,
          attachments: document.general.attachments,
          title: document.title,
          requested_hours: document.final_grade.requested_hours,
          shifts: document.final_grade.number_of_shifts,
          main_author_name: documentSpecification.subtype.ceric.main_author_name,
          main_author_email: documentSpecification.subtype.ceric.main_author_email,
        };
        /* eslint-enable camelcase */
        if (this.cericProposalParameters.infrastructure === 'synchrotron') {
          this.$set(this.cericProposalParameters, 'beamlines', document.general.beamlines);
          this.$set(this.cericProposalParameters, 'end_station', document.general.end_station);
        }
      },
      createProposal() {
        // this method is used if proposal has not been created previously
        // it creates new proposal in db
        this.axios.post('/documents/ceric', this.cericProposalParameters)
          .then((resp) => {
            // if response from axios exists and not includes error then there is induced
            // method 'handleSuccessProposalSave'
            // otherwise there is emitted submission-state=false which prevents modal's closure
            if (!resp) {
              this.$notify({ type: 'error', title: 'Connection error' });
              this.$emit('submission-state', false);
            } else if (!resp.data.error) {
              this.handleSuccessProposalCreate(resp.data);
            } else {
              this.$notify({ type: 'error', title: resp.data.error });
              this.$emit('submission-state', false);
            }
          });
      },
      updateProposal() {
        // this method is used if proposal has been given the _id (objectId) yet
        // it overwrites proposal data (apart from attachments)
        this.axios.put(`/documents/ceric/${this.objectId}`, this.cericProposalParameters)
          .then((resp) => {
            // if response from axios exists and not includes error then there is induced
            // method 'handleSuccessProposalSave'
            // otherwise there is emitted submission-state=false which prevents modal's closure
            const { error } = resp.data;
            if (!error) {
              this.handleSuccessProposalUpdate();
            } else {
              this.$notify({ type: 'error', title: error });
              this.$emit('submission-state', false);
            }
          });
      },
      async handleSuccessProposalCreate(objectId) {
        await this.saveAttachments(objectId);
      },
      async handleSuccessProposalUpdate() {
        await this.handlePDF(this.objectId);
      },
      async saveAttachments(documentId) {
        this.isAttachmentResponse = true;

        await this.cericProposalParameters.attachments.reduce(
          async (promise, attachment) => {
            await promise;

            const { data: { msg } = {} } = await this.axios.put(
              `/documents/${documentId}/attachments/${attachment.name}`,
              attachment,
              { headers: { 'Content-Type': attachment.type } },
            );

            if (!msg) {
              this.isAttachmentResponse = false;
            }
          },
          Promise.resolve(),
        );
        await this.handleSuccessAttachmentsSave(documentId);
      },
      async handleSuccessAttachmentsSave(objectId) {
        if (this.isAttachmentResponse) {
          await this.handlePDF(objectId);
        } else {
          this.$notify({
            type: 'error',
            title: 'Attachments cannot be saved, contact SOLARIS User Office, please',
          });
          this.$emit('submission-state', false);
        }
      },
      async handlePDF(objectId) {
        await this.generatePdf(objectId);
        this.handlePdfResponse();
      },
      async generatePdf(documentId) {
        this.axios.put(`/add-rendered-pdf/${documentId}`)
          .then((response) => {
            this.isPdfResponse = Object.keys(response.data)
              .includes('msg');
          })
          .catch(() => {
            this.isPdfResponse = false;
          });
      },
      handlePdfResponse() {
        if (this.isPdfResponse) {
          this.$notify({ type: 'success', title: 'Saved successful' });
          this.$emit('submission-state', true);
        } else {
          this.$notify({
            type: 'error',
            title: 'PDF cannot be generated, contact SOLARIS User Office, please',
          });
          this.$emit('submission-state', false);
        }
      },
      setInfrastructure() {
        this.cericProposalParameters.infrastructure = String(this.$route.path.split('/').pop());
      },
      customizeProposalFields() {
        if (this.cericProposalParameters.infrastructure === 'synchrotron') {
          this.$set(this.cericProposalParameters, 'beamlines', '');
          this.$set(this.cericProposalParameters, 'end_station', '');
        }
      },
      watchTouchValidatorOnce() {
        // Assign watcher to destroyer and call destroyer after first watcher start.
        // This allows watcher to run only once
        this.unwatch = this.$watch(
          'touchValidator',
          () => {
            this.$v.$touch();
            this.unwatch();
          },
        );
      },
    },
    created() {
      this.watchTouchValidatorOnce();
      if (this.objectId) {
        // eslint-disable-next-line camelcase
        this.attachment_url = `/documents/${this.objectId}`;
        this.input_fields.document_id.disabled = true;
        this.getProposal();
      }
      this.setInfrastructure();
      this.customizeProposalFields();
    },
  };
</script>

<style scoped>
  .error-text {
    margin-top: 18px;
  }
</style>
